import { useState, useEffect } from 'react'
import {
  Link2, CheckCircle, AlertCircle, Clock, RefreshCw, Trash2,
  ArrowRight, Key, Globe, Webhook, FileText, X,
} from 'lucide-react'

interface ProviderInfo {
  id: string
  name: string
  kind: 'oauth2' | 'api_key' | 'webhook_only'
  wave: 1 | 2 | 3 | 4 | 5
  requires_credentials: boolean
  supports_oauth: boolean
}

interface ConnectedIntegration {
  integration_id: string
  provider: string
  status: 'active' | 'paused' | 'error'
  last_sync_at: number | null
  last_sync_status: string | null
  note_count: number
  created_at: number
}

const WAVE_LABELS: Record<number, string> = {
  1: 'Wave 1 — Research Repositories',
  2: 'Wave 2 — Recorders & Transcripts',
  3: 'Wave 3 — Surveys',
  4: 'Wave 4 — Knowledge Bases',
  5: 'Wave 5 — Analytics & Outcomes',
}

const WAVE_DESCRIPTIONS: Record<number, string> = {
  1: 'Import insights and themes from research repositories.',
  2: 'Pull transcripts from call recorders and meeting tools.',
  3: 'Sync survey responses for behavioral analysis.',
  4: 'Extract research context from docs and wikis.',
  5: 'Close the calibration loop with real behavioral and outcome data.',
}

function KindBadge({ kind }: { kind: ProviderInfo['kind'] }) {
  if (kind === 'oauth2') return <span className="flex items-center gap-1 text-xs text-blue-600"><Globe className="w-3 h-3" />OAuth</span>
  if (kind === 'api_key') return <span className="flex items-center gap-1 text-xs text-amber-600"><Key className="w-3 h-3" />API key</span>
  return <span className="flex items-center gap-1 text-xs text-gray-500"><Webhook className="w-3 h-3" />Webhook</span>
}

function StatusDot({ status }: { status: ConnectedIntegration['status'] }) {
  if (status === 'active') return <span className="flex items-center gap-1 text-xs text-emerald-600"><CheckCircle className="w-3.5 h-3.5" />Connected</span>
  if (status === 'error') return <span className="flex items-center gap-1 text-xs text-red-600"><AlertCircle className="w-3.5 h-3.5" />Error</span>
  return <span className="flex items-center gap-1 text-xs text-gray-400"><Clock className="w-3.5 h-3.5" />Paused</span>
}

export function IntegrationsPage() {
  const [providers, setProviders] = useState<ProviderInfo[]>([])
  const [connections, setConnections] = useState<ConnectedIntegration[]>([])
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState<string | null>(null)
  const [syncResult, setSyncResult] = useState<Record<string, number>>({})
  const [apiKeyInputs, setApiKeyInputs] = useState<Record<string, string>>({})
  const [connecting, setConnecting] = useState<string | null>(null)
  const [banner, setBanner] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const integration = params.get('integration')
    const error = params.get('error')
    if (integration === 'connected') {
      setBanner({ kind: 'success', msg: 'Integration connected. Click Sync to import content.' })
      window.history.replaceState({}, '', window.location.pathname)
    } else if (error) {
      setBanner({ kind: 'error', msg: decodeURIComponent(error) })
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])

  const loadData = () =>
    Promise.all([
      fetch('/api/v1/integrations/providers').then(r => r.ok ? r.json() : []),
      fetch('/api/v1/integrations').then(r => r.ok ? r.json() : []),
    ]).then(([p, c]) => {
      setProviders(p as ProviderInfo[])
      setConnections(c as ConnectedIntegration[])
    }).catch(() => {}).finally(() => setLoading(false))

  useEffect(() => { loadData() }, [])

  const getConnection = (providerId: string) =>
    connections.find(c => c.provider === providerId)

  const handleOAuth = (providerId: string) => {
    const token = localStorage.getItem('echo_session')
    const qs = token ? `?token=${encodeURIComponent(token)}` : ''
    window.location.href = `/api/v1/integrations/${providerId}/authorize${qs}`
  }

  const handleApiKeyConnect = async (provider: ProviderInfo) => {
    const key = apiKeyInputs[provider.id]?.trim()
    if (!key) return
    setConnecting(provider.id)
    try {
      await fetch('/api/v1/integrations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider: provider.id, config: { api_key: key } }),
      })
      const updated = await fetch('/api/v1/integrations').then(r => r.json()) as ConnectedIntegration[]
      setConnections(updated)
      setApiKeyInputs(prev => ({ ...prev, [provider.id]: '' }))
      setBanner({ kind: 'success', msg: `${provider.name} connected. Click Sync to import content.` })
    } catch {
      // ignore
    } finally {
      setConnecting(null)
    }
  }

  const handleSync = async (conn: ConnectedIntegration) => {
    setSyncing(conn.integration_id)
    setSyncResult(prev => ({ ...prev, [conn.integration_id]: -1 }))
    try {
      const res = await fetch(`/api/v1/integrations/${conn.integration_id}/sync`, { method: 'POST' })
      const data = await res.json() as { evidence_imported?: number }
      const imported = data.evidence_imported ?? 0
      setSyncResult(prev => ({ ...prev, [conn.integration_id]: imported }))
      const updated = await fetch('/api/v1/integrations').then(r => r.json()) as ConnectedIntegration[]
      setConnections(updated)
    } catch {
      setSyncResult(prev => ({ ...prev, [conn.integration_id]: -2 }))
    } finally {
      setSyncing(null)
    }
  }

  const handleDisconnect = async (integrationId: string) => {
    await fetch(`/api/v1/integrations/${integrationId}`, { method: 'DELETE' })
    setConnections(prev => prev.filter(c => c.integration_id !== integrationId))
  }

  const waves = [1, 2, 3, 4, 5] as const

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Evidence Integrations</h1>
        <p className="text-sm text-gray-500 mt-1">
          Connect research tools, recorders, surveys, and outcome data. Synced content becomes evidence context for pre-reads.
        </p>
      </div>

      {banner && (
        <div className={`flex items-start justify-between gap-3 rounded-xl px-4 py-3 text-sm border ${
          banner.kind === 'success'
            ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <div className="flex items-center gap-2">
            {banner.kind === 'success'
              ? <CheckCircle className="w-4 h-4 shrink-0" />
              : <AlertCircle className="w-4 h-4 shrink-0" />}
            <span>{banner.msg}</span>
          </div>
          <button onClick={() => setBanner(null)} className="shrink-0 mt-0.5 text-current opacity-60 hover:opacity-100">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="h-28 bg-gray-100 rounded-xl animate-pulse" />)}
        </div>
      ) : (
        waves.map(wave => {
          const waveProviders = providers.filter(p => p.wave === wave)
          if (waveProviders.length === 0) return null

          return (
            <section key={wave}>
              <div className="mb-4">
                <h2 className="text-sm font-semibold text-gray-800">{WAVE_LABELS[wave]}</h2>
                <p className="text-xs text-gray-400 mt-0.5">{WAVE_DESCRIPTIONS[wave]}</p>
              </div>

              <div className="grid grid-cols-1 gap-3">
                {waveProviders.map(provider => {
                  const conn = getConnection(provider.id)
                  const isRequiresCredentials = provider.requires_credentials && !conn
                  const lastSyncResult = conn ? syncResult[conn.integration_id] : undefined
                  const noteCount = conn?.note_count ?? 0

                  return (
                    <div key={provider.id} className="bg-white border border-gray-200 rounded-xl p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1">
                            <span className="font-medium text-sm text-gray-900">{provider.name}</span>
                            <KindBadge kind={provider.kind} />
                            {conn && <StatusDot status={conn.status} />}
                          </div>

                          {conn && (
                            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400">
                              <span>
                                Last sync: {conn.last_sync_at ? new Date(conn.last_sync_at).toLocaleDateString() : 'Never'}
                                {conn.last_sync_status && ` · ${conn.last_sync_status}`}
                              </span>
                              {noteCount > 0 && (
                                <span className="flex items-center gap-1 text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
                                  <FileText className="w-3 h-3" />
                                  {noteCount} {noteCount === 1 ? 'page' : 'pages'} in evidence library
                                </span>
                              )}
                              {lastSyncResult !== undefined && lastSyncResult === -1 && (
                                <span className="text-gray-400">Syncing…</span>
                              )}
                              {lastSyncResult !== undefined && lastSyncResult >= 0 && (
                                <span className="text-emerald-600">
                                  +{lastSyncResult} imported this sync
                                </span>
                              )}
                              {lastSyncResult === -2 && (
                                <span className="text-red-500">Sync failed</span>
                              )}
                            </div>
                          )}

                          {isRequiresCredentials && provider.kind === 'api_key' && (
                            <div className="flex items-center gap-2 mt-2">
                              <input
                                type="password"
                                value={apiKeyInputs[provider.id] ?? ''}
                                onChange={e => setApiKeyInputs(prev => ({ ...prev, [provider.id]: e.target.value }))}
                                placeholder="Paste API key…"
                                className="flex-1 text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-echo-indigo/30 max-w-56"
                                onKeyDown={e => e.key === 'Enter' && handleApiKeyConnect(provider)}
                              />
                              <button
                                onClick={() => handleApiKeyConnect(provider)}
                                disabled={connecting === provider.id || !apiKeyInputs[provider.id]?.trim()}
                                className="text-xs px-3 py-1.5 bg-echo-indigo text-white rounded-lg disabled:opacity-40 hover:bg-echo-indigo/90 transition-colors"
                              >
                                {connecting === provider.id ? 'Connecting…' : 'Connect'}
                              </button>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          {!conn && provider.supports_oauth && (
                            <button
                              onClick={() => handleOAuth(provider.id)}
                              className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-echo-indigo text-white rounded-lg hover:bg-echo-indigo/90 transition-colors"
                            >
                              Connect <ArrowRight className="w-3 h-3" />
                            </button>
                          )}

                          {conn && (
                            <>
                              <button
                                onClick={() => handleSync(conn)}
                                disabled={syncing === conn.integration_id}
                                className="flex items-center gap-1 text-xs px-2.5 py-1.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 disabled:bg-button-disabled-bg disabled:text-button-disabled-text disabled:border-button-disabled-border disabled:cursor-not-allowed transition-colors"
                                title="Sync now"
                              >
                                <RefreshCw className={`w-3.5 h-3.5 ${syncing === conn.integration_id ? 'animate-spin' : ''}`} />
                                {syncing === conn.integration_id ? 'Syncing…' : 'Sync'}
                              </button>
                              <button
                                onClick={() => handleDisconnect(conn.integration_id)}
                                className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors"
                                title="Disconnect"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </>
                          )}

                          {!conn && !provider.supports_oauth && provider.kind !== 'api_key' && (
                            <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                              Coming soon
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </section>
          )
        })
      )}

      {connections.length === 0 && !loading && (
        <div className="text-center py-8 text-gray-400">
          <Link2 className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No integrations connected yet. Connect one above to start importing evidence.</p>
        </div>
      )}

      {connections.length > 0 && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 text-sm text-indigo-800">
          <p className="font-medium mb-1">How evidence flows into pre-reads</p>
          <p className="text-xs text-indigo-600 leading-relaxed">
            Synced content is stored in your evidence library. When you run a pre-read, Echosome retrieves
            relevant notes and uses them as grounding context for each ensemble pass — the more evidence
            you import, the higher the confidence scores on segment predictions.
          </p>
        </div>
      )}
    </div>
  )
}
